import { MonoTypeOperatorFunction, Observable, throwError, timer } from 'rxjs';
import { mergeMap, retryWhen } from 'rxjs/operators';

export interface RetryConfig {
  maxRetries?: number;
  baseDelay?: number;
  shouldRetry?: (error: any) => boolean;
}

/**
 * Custom retry operator with exponential backoff and smart error handling
 */
export function retryWithBackoff<T>(config: RetryConfig = {}): MonoTypeOperatorFunction<T> {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    shouldRetry = (error: any) => {
      // Don't retry on client errors (4xx)
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      // Retry on server errors, network issues, and timeouts
      return error?.status >= 500 || error?.status === 0 || error?.status === 408;
    }
  } = config;

  return retryWhen((errors) => {
    return errors.pipe(
      mergeMap((error, i) => {
        const retryAttempt = i + 1;
        
        // Check if we should retry and haven't exceeded max retries
        if (retryAttempt <= maxRetries && shouldRetry(error)) {
          // Calculate exponential backoff delay with jitter
          const delay = calculateBackoffDelay(retryAttempt, baseDelay);
          
          console.warn(`🔄 Retrying request (${retryAttempt}/${maxRetries}) after ${delay}ms:`, error.message || 'Unknown error');
          
          return timer(delay);
        } else {
          // Max retries exceeded or non-retryable error
          if (retryAttempt > maxRetries) {
            console.error(`🚫 Max retries (${maxRetries}) exceeded for request. Final error:`, error);
          }
          return throwError(() => error);
        }
      })
    );
  });
}

/**
 * Calculate exponential backoff delay with jitter
 */
function calculateBackoffDelay(attempt: number, baseDelay: number): number {
  const maxDelay = 30000; // 30 seconds max
  const exponentialDelay = baseDelay * Math.pow(2, attempt - 1);
  const jitter = exponentialDelay * 0.1 * Math.random(); // Add 10% jitter
  
  return Math.min(exponentialDelay + jitter, maxDelay);
}

/**
 * Create a timeout operator that works with the retry logic
 */
export function timeoutWithFallback<T>(timeoutMs: number, fallbackMessage?: string) {
  return (source: Observable<T>) => 
    new Observable<T>(observer => {
      let hasCompleted = false;
      
      // Set up timeout
      const timeout = setTimeout(() => {
        if (!hasCompleted) {
          const error = {
            status: 408,
            message: fallbackMessage || `Request timed out after ${timeoutMs}ms`,
            timeout: true
          };
          observer.error(error);
        }
      }, timeoutMs);
      
      // Subscribe to source
      const subscription = source.subscribe({
        next: (value) => {
          hasCompleted = true;
          clearTimeout(timeout);
          observer.next(value);
        },
        error: (error) => {
          hasCompleted = true;
          clearTimeout(timeout);
          observer.error(error);
        },
        complete: () => {
          hasCompleted = true;
          clearTimeout(timeout);
          observer.complete();
        }
      });
      
      return () => {
        clearTimeout(timeout);
        subscription.unsubscribe();
      };
    });
}